import cuid from 'cuid'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { SOURCE_SCOPE } from '@disco/constants/search'
import { fetchSuggestedTracks } from '@disco/state-management/actions/search-v2-actions'
import captureError from '@disco/utils/captureError'
import { isPersistedAudioTrack } from '@disco/utils/tracks'

import type { V2SearchQuery } from 'types/search-page'
import type { Track, TrackId } from 'types/track'

import { useDispatch } from 'hooks/use-redux'

/** How many suggestions are shown at once. */
export const SUGGESTED_TRACKS_COUNT = 3
// Fetch several batches per request so added suggestions are replaced and
// Refresh shows the next batch without a round trip, and filtered-out hits
// rarely leave a batch empty.
const SEARCH_PAGE_SIZE = SUGGESTED_TRACKS_COUNT * 4
// AIMS accepts at most this many comparison tracks.
const MAX_AIMS_SEED_TRACKS = 10

const NO_TRACKS: Track[] = []
const NO_TRACK_IDS: ReadonlySet<TrackId> = new Set()

type SearchRequest = {
  payload: V2SearchQuery
  contentKey: string
  referenceName?: string
}
type Request = SearchRequest & { excludedTrackIds: TrackId[] }
type Suggestions = {
  request: Request
  // Unseen results; the first SUGGESTED_TRACKS_COUNT not yet excluded are shown.
  tracks: Track[]
  error: boolean
  nextCursor: string | null
}
type Options = {
  /** Whether searches may start on their own, e.g. false while the panel is collapsed. */
  enabled?: boolean
  /** Tracks never to suggest, such as those already in the playlist. */
  excludedTrackIds?: ReadonlySet<TrackId>
}

const sortIds = (ids: TrackId[]) => [...ids].sort((a, b) => a - b)
const uniqueIds = (tracks: Track[]) => [...new Set(tracks.map(t => t.id))]

// The search rejects the whole request when any seed can't be played in the
// user's territories, so leave those out.
const isUsableSeed = (track: Track) => !track.outside_your_territories

// Always include the last added track so AIMS reflects it even after the
// playlist has been reordered.
const getAimsSeedIds = (ids: TrackId[], lastAddedTrack: Track | undefined) => {
  if (!lastAddedTrack || !ids.includes(lastAddedTrack.id))
    return ids.slice(-MAX_AIMS_SEED_TRACKS)
  const others = ids.filter(id => id !== lastAddedTrack.id)
  return [...others.slice(-(MAX_AIMS_SEED_TRACKS - 1)), lastAddedTrack.id]
}

const createSearchRequest = (
  lastAddedTrack: Track | undefined,
  playlistTracks: Track[],
  aimsEnabled: boolean,
): SearchRequest | undefined => {
  const audioTracks = playlistTracks.filter(isPersistedAudioTrack)
  const seeds = audioTracks.filter(isUsableSeed)
  // Our own SimSearch takes a single seed: the newest track when it can be used.
  const ownSeed =
    seeds.find(t => t.id === lastAddedTrack?.id) ?? seeds[seeds.length - 1]
  if (!ownSeed) return

  const seedIds = aimsEnabled
    ? getAimsSeedIds(uniqueIds(seeds), lastAddedTrack)
    : [ownSeed.id]
  return {
    payload: {
      query: '',
      similar_to_track_ids: sortIds(seedIds),
      type: 'simsearch',
      size: SEARCH_PAGE_SIZE,
      scope: [SOURCE_SCOPE.BUSINESS],
      sort: ['-score'],
      result_types: ['track'],
      event_data: { context: 'Playlist Creator', search_id: cuid() },
    },
    contentKey: JSON.stringify([
      aimsEnabled,
      sortIds(uniqueIds(audioTracks)),
      lastAddedTrack?.id,
    ]),
    referenceName: aimsEnabled ? undefined : ownSeed.name,
  }
}

/**
 * Loads tracks similar to a playlist's, a few at a time, favouring the last
 * added track.
 */
export const useTrackSuggestions = (
  lastAddedTrack: Track | undefined,
  playlistTracks: Track[],
  aimsEnabled: boolean,
  { enabled = true, excludedTrackIds = NO_TRACK_IDS }: Options = {},
) => {
  const dispatch = useDispatch()
  const search = useMemo(
    () => createSearchRequest(lastAddedTrack, playlistTracks, aimsEnabled),
    [lastAddedTrack, playlistTracks, aimsEnabled],
  )
  const [request, setRequest] = useState<Request>()
  const [suggestions, setSuggestions] = useState<Suggestions>()

  useEffect(() => {
    // An emptied playlist starts over, so its next track loads suggestions again.
    if (!lastAddedTrack) setRequest(undefined)
    else if (enabled && !request && search)
      setRequest({ ...search, excludedTrackIds: [...excludedTrackIds] })
  }, [lastAddedTrack, enabled, request, search, excludedTrackIds])

  useEffect(() => {
    if (!request) return

    const controller = new AbortController()
    dispatch(
      fetchSuggestedTracks(
        request.payload,
        controller.signal,
        request.excludedTrackIds,
      ),
    )
      .then(({ tracks, nextCursor }) => {
        if (controller.signal.aborted) return
        setSuggestions({ request, tracks, error: false, nextCursor })
      })
      .catch(error => {
        if (controller.signal.aborted) return
        captureError(error, {
          description: 'Failed to load suggested tracks',
          extra: { level: 'info' },
        })
        setSuggestions({ request, tracks: [], error: true, nextCursor: null })
      })

    return () => controller.abort()
  }, [dispatch, request])

  const current = suggestions?.request === request ? suggestions : undefined
  // Adding a shown track brings up the next one from the same results.
  const remainingTracks = useMemo(
    () =>
      current?.tracks.filter(track => !excludedTrackIds.has(track.id)) ??
      NO_TRACKS,
    [current, excludedTrackIds],
  )
  const visibleTracks = useMemo(
    () => remainingTracks.slice(0, SUGGESTED_TRACKS_COUNT),
    [remainingTracks],
  )
  const nextBatch = useMemo(
    () => remainingTracks.slice(SUGGESTED_TRACKS_COUNT),
    [remainingTracks],
  )
  const seedReady = Boolean(search)
  const loading = seedReady && !current
  const error = seedReady && Boolean(current?.error)
  const hasMore = nextBatch.length > 0 || Boolean(current?.nextCursor)
  const contentChanged = search?.contentKey !== request?.contentKey
  const canRefresh =
    seedReady && !loading && (contentChanged || (!error && hasMore))
  // Every fetched suggestion has been added, so load more.
  const refilling =
    enabled &&
    canRefresh &&
    Boolean(current?.tracks.length) &&
    !remainingTracks.length

  const refresh = useCallback(() => {
    if (!search || !request || !current || !canRefresh) return
    if (contentChanged) {
      setRequest({ ...search, excludedTrackIds: [...excludedTrackIds] })
    } else if (nextBatch.length > 0) {
      setSuggestions({ ...current, tracks: nextBatch })
    } else {
      // A next page continues the same search, keeping its seeds and analytics id.
      setRequest({
        ...request,
        payload: {
          ...request.payload,
          cursor: current.nextCursor ?? undefined,
        },
        excludedTrackIds: [...excludedTrackIds],
      })
    }
  }, [
    search,
    request,
    current,
    canRefresh,
    contentChanged,
    nextBatch,
    excludedTrackIds,
  ])

  useEffect(() => {
    if (refilling) refresh()
  }, [refilling, refresh])

  return {
    tracks: search ? visibleTracks : NO_TRACKS,
    loading: loading || refilling,
    error,
    canRefresh: canRefresh && !refilling,
    seedReady,
    referenceName: (request ?? search)?.referenceName,
    /** Analytics id of the search the shown tracks came from. */
    searchId: current?.request.payload.event_data?.search_id,
    retry: () => {
      if (request && !loading) setRequest({ ...request })
    },
    refresh,
  }
}
